import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { BarsArrowDownIcon, BarsArrowUpIcon } from "@heroicons/react/24/solid";
import { useFormik } from "formik";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  formatDateTime,
  formatNumericDate,
  formatRoundedAmount,
  formatTime,
  getChangedFields,
  useDebounce,
} from "../../util";
import { getInput, IconButton, ImageZoom, Spinner, Tag } from "../ui";
import {
  ROW_UPDATE_DEBOUNCE_MS,
  sanitiseTableRowUpdate,
} from "./sanitiseTableRowUpdate";
import useSequentialRowUpdates from "./useSequentialRowUpdates";

const SubLabel = props => {
  const { label, widthInPx = 200 } = props || {};
  return (
    <div
      className="text-[15px] italic tracking-wide text-slate-500"
      style={{ maxWidth: `${widthInPx}px` }}
    >
      {label}
    </div>
  );
};

const Label = props => {
  const { label, labelClass, widthInPx = 200 } = props || {};
  const className =
    typeof labelClass == "function" ? labelClass?.(props) : labelClass;
  return (
    <div
      title={label}
      className={`overflow-hidden text-ellipsis text-base font-medium leading-5 text-black ${className && className}`}
      style={{ maxWidth: `${widthInPx}px` }}
    >
      {label}
    </div>
  );
};

const Link = props => {
  const { label, labelClass, widthInPx = 200, to, id, values } = props || {};
  const navigate = useNavigate();
  const location = useLocation();
  const className =
    typeof labelClass == "function" ? labelClass?.(props) : labelClass;

  if (!to) {
    return (
      <div
        title={label}
        className={`overflow-hidden text-ellipsis text-base font-medium leading-5 ${className && className}`}
        style={{ maxWidth: `${widthInPx}px` }}
      >
        {label}
      </div>
    );
  }

  const path =
    typeof to === "function" ? to({ id, values }) : to.replace(":id", id);

  // Ensure path is absolute for href (starts with /) to work correctly when opening in new tab
  // If path is relative, combine it with the current location's pathname
  const getAbsoluteHref = () => {
    if (path.startsWith("/") || path.startsWith("http")) {
      return path;
    }
    // Remove query params from current pathname and combine with relative path
    const basePath = location.pathname.split("?")[0];
    // Remove trailing slash from base and ensure path starts with /
    const cleanBase = basePath.replace(/\/$/, "");
    const cleanPath = path.startsWith("/") ? path : `/${path}`;
    return `${cleanBase}${cleanPath}`;
  };

  const href = getAbsoluteHref();

  // Use original path for navigate (React Router handles relative paths)
  const navigatePath = path;

  const handleClick = event => {
    // Allow default behavior for modifier keys (Ctrl/Cmd+click, middle-click, etc.)
    if (event.ctrlKey || event.metaKey || event.button === 1) {
      return;
    }

    // Prevent default for normal clicks and use navigate
    event.preventDefault();
    navigate(navigatePath);
  };

  return (
    <a
      href={href}
      title={label}
      className={`cursor-pointer overflow-hidden text-ellipsis text-base font-medium leading-5 underline hover:text-gray-500 ${className && className}`}
      style={{ maxWidth: `${widthInPx}px` }}
      onClick={handleClick}
      onAuxClick={handleClick}
    >
      {label}
    </a>
  );
};

const CurrencyLabel = props => {
  const { label, labelClass, widthInPx = 200 } = props || {};
  const className =
    typeof labelClass == "function" ? labelClass?.(props) : labelClass;
  return (
    <div
      title={label}
      className={`overflow-hidden text-ellipsis text-base font-medium leading-5 text-black ${className && className}`}
      style={{ maxWidth: `${widthInPx}px` }}
    >
      {label
        ? new Intl.NumberFormat("en-GB", {
            minimumFractionDigits: 2,
          }).format(label)
        : ""}
    </div>
  );
};

const MultiLabel = props => {
  const { to, id, values, name, formik } = props || {};
  return (
    <div className="flex flex-col">
      {name?.map((item, index) => {
        if (typeof item === "function") {
          return item?.(props);
        } else {
          const value = formik?.getFieldProps?.(item)?.value || "";
          return index === 0 ? (
            to ? (
              <Link key={index} label={value} to={to} id={id} values={values} />
            ) : (
              <Label key={index} label={value} />
            )
          ) : (
            <SubLabel key={index} label={value} />
          );
        }
      })}
    </div>
  );
};

const Image = props => {
  const { label: imageSrc, formik, name, disableZoom, onClick } = props || {};
  const [showFallback, setShowFallback] = useState(!imageSrc);

  // Derive imgX path from imgT path (e.g., "edition.artwork.imagesSummary.imgT" -> "edition.artwork.imagesSummary.imgX")
  // Also handles array paths like "instance.edition.artwork.images[0].imgT"
  const getZoomSrc = useCallback(() => {
    if (!name || !formik?.values) return imageSrc || "";

    const imgXPath = name.replace(/\.imgT$/, ".imgX");
    const pathParts = imgXPath.split(".");
    let value = formik.values;

    for (const part of pathParts) {
      if (!value || typeof value !== "object") return imageSrc || "";

      // Handle array indices like "images[0]"
      const arrayMatch = part.match(/^(\w+)\[(\d+)\]$/);
      if (arrayMatch) {
        const [, arrayKey, index] = arrayMatch;
        if (
          Array.isArray(value[arrayKey]) &&
          value[arrayKey][parseInt(index)]
        ) {
          value = value[arrayKey][parseInt(index)];
        } else {
          return imageSrc || "";
        }
      } else if (part in value) {
        value = value[part];
      } else {
        return imageSrc || "";
      }
    }

    return value || imageSrc || "";
  }, [name, formik?.values, imageSrc]);

  const resolvedSrc = imageSrc || "";
  const zoomSrc = getZoomSrc();

  useEffect(() => {
    setShowFallback(!resolvedSrc);
  }, [resolvedSrc]);

  const handleImageError = () => {
    setShowFallback(true);
  };

  const handleImageLoad = () => {
    setShowFallback(false);
  };

  const handleCellClick = useCallback(
    event => {
      if (typeof onClick !== "function") return;
      event.stopPropagation();
      onClick(props);
    },
    [onClick, props],
  );

  return (
    <div
      className={`group relative flex h-[50px] w-[50px] items-center justify-center overflow-hidden border border-gray-300 ${showFallback ? "bg-gray-100" : ""} ${onClick ? "cursor-pointer" : ""}`}
    >
      {!showFallback && resolvedSrc ? (
        disableZoom ? (
          <img
            alt="Artwork thumbnail"
            className="h-full w-full object-cover"
            src={resolvedSrc}
            onError={handleImageError}
            onLoad={handleImageLoad}
          />
        ) : (
          <ImageZoom
            alt="Artwork thumbnail"
            className="h-full w-full"
            onClick={onClick ? handleCellClick : undefined}
            onImageError={handleImageError}
            onImageLoad={handleImageLoad}
            src={resolvedSrc}
            zoomSrc={zoomSrc !== resolvedSrc ? zoomSrc : undefined}
          />
        )
      ) : (
        <span className="h-max text-center text-xs text-gray-500">
          NO
          <br />
          IMAGE
        </span>
      )}
    </div>
  );
};

const getType = props => {
  const { defaultValue, formik, name, type, component, disableZoom } =
    props || {};
  const values = formik?.values || {};
  const value = formik?.getFieldProps?.(name)?.value || defaultValue || "";
  const cProps = {
    ...props,
    id: values?.id,
    label: value,
    values,
  };

  switch (type) {
    case "custom":
      return component(cProps);
    case "currency":
      return (
        <Label
          label={
            value
              ? value?.toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
              : "0.00"
          }
        />
      );
    case "currency-rounded":
      return <Label label={formatRoundedAmount(value)} />;
    case "date":
      return (
        <Label
          label={
            value ? moment.parseZone(value).utc().format("D MMM YYYY") : ""
          }
        />
      );
    case "date-numeric":
      return <Label {...cProps} label={formatNumericDate(value)} />;
    case "date-time":
      return <Label label={value ? formatDateTime(value) : ""} />;
    case "time":
      return <Label label={value ? formatTime(value) : ""} />;
    case "image": {
      return <Image {...cProps} disableZoom={disableZoom} />;
    }
    case "input":
      return getInput({
        ...cProps,
        type: cProps?.inputType,
        label: null,
      });
    case "label":
      return <Label {...cProps} />;
    case "link":
      return <Link {...cProps} />;
    case "multi-label":
      return <MultiLabel {...cProps} />;
    case "tag":
      // eslint-disable-next-line
      const tagProps = {
        ...cProps,
        action: formik?.values?.action,
      };
      return <Tag {...tagProps} />;
    case "currencylabel":
      return <CurrencyLabel {...cProps} />;
    default:
      return null;
  }
};

const Cell = props => {
  const { item, fnProps } = props || {};
  let executableItem = item;

  if (typeof item === "function") {
    executableItem = item?.(fnProps);
  }
  const { onClick } = executableItem || {};
  const component = getType({ ...fnProps, ...executableItem });

  const allowColumnWrap = fnProps?.allowColumnWrap === true;
  const allowHorizontalScroll = fnProps?.allowHorizontalScroll === true;
  const columnWantsWrap = executableItem?.wrap === true;

  const cellClasses = [
    "border-0 border-b border-gray-200 leading-5",
    onClick ? "cursor-pointer hover:underline" : "",
    "containter-column p-2",
    allowColumnWrap && !allowHorizontalScroll
      ? "overflow-hidden min-[1280px]:overflow-visible"
      : "",
    allowColumnWrap && columnWantsWrap
      ? allowHorizontalScroll
        ? "break-words whitespace-normal"
        : "min-[1280px]:break-words"
      : "whitespace-nowrap",
    executableItem?.rowClass || "",
  ]
    .filter(Boolean)
    .join(" ");

  const content = allowColumnWrap ? (
    <div
      className={
        columnWantsWrap
          ? allowHorizontalScroll
            ? "min-w-0 break-words"
            : "line-clamp-1 min-w-0 overflow-hidden min-[1280px]:line-clamp-none min-[1280px]:overflow-visible min-[1280px]:break-words"
          : allowHorizontalScroll
            ? "min-w-0 whitespace-nowrap"
            : "min-w-0 overflow-hidden text-ellipsis whitespace-nowrap"
      }
    >
      {component}
    </div>
  ) : (
    component
  );

  return (
    <td onClick={() => onClick?.(fnProps)} className={cellClasses}>
      {content}
    </td>
  );
};

const Row = props => {
  const {
    headers,
    row,
    updateMutation,
    onMutationError,
    rowClassName,
    showNewDataColumn,
    allowColumnWrap,
    allowHorizontalScroll,
    sequentialRowUpdates,
  } = props || {};

  const formik = useFormik({
    initialValues: {
      ...row,
    },
    enableReinitialize: !sequentialRowUpdates,
  });

  const debouncedValues = useDebounce(formik?.values, ROW_UPDATE_DEBOUNCE_MS);
  const debouncedInitialValues = useDebounce(
    formik?.initialValues,
    ROW_UPDATE_DEBOUNCE_MS,
  );

  useSequentialRowUpdates({
    debouncedValues,
    enabled: sequentialRowUpdates,
    formik,
    headers,
    onMutationError,
    row,
    updateMutation,
  });

  useEffect(() => {
    if (sequentialRowUpdates) {
      return;
    }

    const nextChangedFields = getChangedFields(
      debouncedValues || {},
      debouncedInitialValues || {},
    );
    const pruned = sanitiseTableRowUpdate(nextChangedFields, headers);
    if (Object.keys(pruned).length === 0) {
      return;
    }
    updateMutation?.({
      variables: {
        input: {
          id: formik?.values?.id,
          ...pruned,
        },
      },
    }).catch(error => {
      onMutationError?.(error);
    });
  }, [
    debouncedValues,
    debouncedInitialValues,
    updateMutation,
    formik?.values?.id,
    headers,
    onMutationError,
    sequentialRowUpdates,
  ]);

  const fnProps = {
    formik,
    allowColumnWrap,
    allowHorizontalScroll,
    ...props,
  };

  // Compute the row class name
  const computedRowClassName =
    typeof rowClassName === "function"
      ? rowClassName(formik?.values)
      : rowClassName || "";

  return (
    <tr
      className={`component-table-row group border-0 border-b border-gray-400 hover:bg-gray-50 ${computedRowClassName}`}
    >
      {showNewDataColumn && (
        <td className="w-4 bg-white p-1">
          {formik?.values?.isnewData && (
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sky-400 opacity-75"></span>
              <span className="relative inline-flex h-2 w-2 rounded-full bg-sky-500"></span>
            </span>
          )}
        </td>
      )}
      {headers?.map((item, index) => (
        <Cell key={index} item={item} fnProps={fnProps} />
      ))}
    </tr>
  );
};

const Table = props => {
  const {
    formik,
    headers = [],
    loading,
    rows,
    updateMutation,
    variant = "default",
    rowClassName,
    showNewDataColumn = false,
    allowColumnWrap = false,
    allowHorizontalScroll = false,
    emptyMessage = "No results found",
    showRefresh = false,
    onRefresh,
    refreshDisabled = false,
    isRefreshing = false,
    onMutationError,
    sequentialRowUpdates = false,
  } = props || {};
  const order = formik?.getFieldProps("order")?.value;

  const variants = {
    default: "min-h-48 md:min-h-[40rem]",
    sm: "h-32",
    md: "h-80",
    lg: "h-90",
    modal: "min-h-48 md:min-h-[37.5rem]",
    dynamicHeight: "",
  };
  const classes = variants[variant];

  const firstHeader = headers?.[0];
  const resolvedFirstHeader =
    typeof firstHeader === "function" ? firstHeader(props) : firstHeader;
  const isFirstHeaderMultiSelect = Boolean(
    resolvedFirstHeader?.isMultiSelectHeader,
  );

  const handleSort = item => {
    const { sortKey } = item;
    const { setValue } = formik.getFieldHelpers("order");

    if (order && order.length === 2) {
      if (order[0] === sortKey) {
        if (order[1] === "ASC") setValue([sortKey, "DESC"]);
        else if (order[1] === "DESC") setValue(null);
      } else setValue([sortKey, "ASC"]);
    } else {
      setValue([sortKey, "ASC"]);
    }
  };

  return (
    <div className="flex min-w-0 flex-col">
      <div
        className={`w-full max-w-full touch-auto overflow-x-auto overflow-y-visible overscroll-x-contain ${
          allowColumnWrap && !allowHorizontalScroll
            ? "min-[1280px]:overflow-x-visible"
            : ""
        }`}
      >
        <div
          className={`inline-block min-w-full py-3 align-middle ${
            allowColumnWrap && !allowHorizontalScroll
              ? "min-[1280px]:block min-[1280px]:w-full"
              : ""
          }`}
        >
          <div className={`relative overflow-visible ${classes}`}>
            {showRefresh && (
              <div className="pointer-events-none absolute right-0 top-0 z-10">
                <IconButton
                  title="Refresh"
                  className="pointer-events-auto bg-white shadow-sm disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={onRefresh}
                  variant="default"
                  disabled={refreshDisabled}
                >
                  <ArrowPathIcon
                    className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`}
                  />
                </IconButton>
              </div>
            )}
            <table
              className={`${
                allowHorizontalScroll
                  ? "w-max min-w-full table-auto"
                  : allowColumnWrap
                    ? "min-w-[48rem] table-fixed min-[1280px]:w-full min-[1280px]:min-w-full min-[1280px]:table-auto"
                    : "min-w-[48rem] table-fixed lg:min-w-full"
              } border-separate divide-y divide-gray-200`}
              style={{ borderSpacing: 0 }}
            >
              <thead className="sticky top-0 z-10 bg-white">
                <tr>
                  {showNewDataColumn && <th className="w-4" aria-hidden />}
                  {headers?.map((i, index) => {
                    let item = i;
                    if (typeof item === "function") {
                      item = item?.(props);
                    }
                    return (
                      <th
                        key={index}
                        scope="col"
                        className={
                          !item?.className &&
                          `border-0 border-b border-gray-200 p-2 text-left text-xs font-bold font-medium text-black ${
                            index === 0 && !isFirstHeaderMultiSelect
                              ? "!pl-0"
                              : ""
                          } ${item?.headerCellClassName || ""}`
                        }
                        {...(item?.sortKey
                          ? { onClick: () => handleSort(item) }
                          : {})}
                        style={{
                          width: item.width || "initial",
                          ...(item.minWidth != null && {
                            minWidth: item.minWidth,
                          }),
                        }}
                      >
                        <div className="flex">
                          <div className={item?.className}>
                            {typeof item?.label === "function" ? (
                              item?.label(props)
                            ) : Array.isArray(item?.label) ? (
                              <div className="flex flex-col">
                                {item?.label?.map((label, index) => (
                                  <div key={index}>{label}</div>
                                ))}
                              </div>
                            ) : (
                              item?.label
                            )}
                          </div>
                          {item?.sortKey && (
                            <div className="ml-4">
                              {order &&
                              order.length == 2 &&
                              order[0] == item?.sortKey ? (
                                order[1] === "ASC" ? (
                                  <BarsArrowUpIcon className="h-4 w-4" />
                                ) : (
                                  <BarsArrowDownIcon className="h-4 w-4" />
                                )
                              ) : null}
                            </div>
                          )}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className={`divide-y divide-gray-200 bg-white`}>
                {loading || rows?.length === 0 ? (
                  <tr>
                    <td colSpan={headers.length + (showNewDataColumn ? 1 : 0)}>
                      <div className="absolute relative inset-0 block flex w-full items-center justify-center py-12 text-black">
                        {loading ? (
                          <Spinner className="text-black" />
                        ) : (
                          <div className="text-black">{emptyMessage}</div>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  rows?.map((row, index) => {
                    const rowProps = {
                      headers,
                      row,
                      updateMutation,
                      rowClassName,
                      showNewDataColumn,
                      allowColumnWrap,
                      allowHorizontalScroll,
                      onMutationError,
                      sequentialRowUpdates,
                    };
                    // Key by stable row id (not array index) so each Row keeps
                    // its own formik state when the list reorders after an
                    // inline edit. Index keys cause edits to bleed onto whatever
                    // row moves into the edited row's position. Fall back to the
                    // index for rows that have no id.
                    const rowKey =
                      row?.id != null ? `row-${row.id}` : `index-${index}`;
                    return <Row key={rowKey} {...rowProps} />;
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Table;
